import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaCamera, FaUser, FaBriefcase, FaPhoneAlt, FaUniversity, FaFileAlt, FaStickyNote, FaPlus, FaTrashAlt, FaCloudUploadAlt  } from "react-icons/fa";

import Dropdown from "../modal/dropdown";
import DatePicker from "../modal/datepicker"; 
import "./employee.css";
import {
  getDepartments,
  getDesignations,
  getReportingManagers,
  getEmployeeById,
  createEmployee,
  updateEmployee,
} from "../services/employeeservice";
import {
  EmployeeStatus,
  GenderOptions,
  DocumentTypeOptions,
  DocumentTypeFormOptions,
} from "../../constants/constants";
import api from "../services/api";
import { parseFieldErrors } from "../../utils/errors";
import { toast } from "react-toastify";

const initialForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  gender: "",
  date_of_birth: "",
  department: "",
  designation: "",
  joining_date: "",
  status: EmployeeStatus.ACTIVE,
  reporting_manager: "",
  shift: "",
  role_ids: [],
  account_number: "",
  ifsc: "",
  bank_name: "",
  emergency_contact_name: "",
  emergency_contact_number: "",
  remarks: "",
  photo: null,
  photoPreview: null,
  documents: [{ type: "", file: null }],
};

const normalizeDocType = (val) => {
  if (!val) return "";
  const raw = String(val).trim();
  const match = DocumentTypeOptions.find(
    (t) =>
      String(t.value).toLowerCase() === raw.toLowerCase() ||
      t.label.toLowerCase() === raw.toLowerCase()
  );
  return match ? match.value : raw;
};

function mapEmployeeToForm(existingEmployee) {
  return {
    ...initialForm,
    first_name: existingEmployee.first_name || "",
    last_name: existingEmployee.last_name || "",
    email: existingEmployee.email || "",
    phone: existingEmployee.phone || "",
    gender: existingEmployee.gender || "",
    date_of_birth: existingEmployee.date_of_birth || existingEmployee.birthday || "",
    department: existingEmployee.department?.id || existingEmployee.department || "",
    designation: existingEmployee.designation?.id || existingEmployee.designation || "",
    joining_date: existingEmployee.date_of_joining || "",
    status: existingEmployee.status || EmployeeStatus.ACTIVE,
    reporting_manager: existingEmployee.reporting_manager?.id || (typeof existingEmployee.reporting_manager === "object" ? "" : existingEmployee.reporting_manager) || "",
    shift: existingEmployee.shift?.id || existingEmployee.shift || "",
    role_ids: (existingEmployee.roles && existingEmployee.roles.length > 0) ? existingEmployee.roles.map(r => typeof r === "object" ? r.id : r) : (existingEmployee.role_id ? [existingEmployee.role_id] : []),
    account_number: existingEmployee.bank_account_number || "",
    ifsc: existingEmployee.ifsc_code || "",
    bank_name: existingEmployee.bank_name || "",
    emergency_contact_name: existingEmployee.emergency_contact_name || "",
    emergency_contact_number: existingEmployee.emergency_contact_number || "",
    remarks: existingEmployee.remarks || "",
    photoPreview: existingEmployee.photo_url || existingEmployee.photo || null,
    documents: existingEmployee.documents?.length
      ? existingEmployee.documents.map((doc) => ({
          ...doc,
          // Keep the original id so updates target the existing record
          // instead of creating a duplicate on save.
          id: doc.id ?? doc.document_id ?? null,
          type: normalizeDocType(doc.document_type || doc.type),
        }))
      : [{ type: "", file: null }],
  };
}

export default function EmployeeFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [existingEmployee, setExistingEmployee] = useState(null);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [form, setForm] = useState(initialForm);

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [shifts, setShifts] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const photoInputRef = useRef(null);

  // Load the employee being edited
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setPageLoading(true);
        const res = await getEmployeeById(id);
        const emp = res.data || res;
        setExistingEmployee(emp);
        setForm(mapEmployeeToForm(emp));
      } catch {
        toast.error("Failed to load employee details");
        navigate("/employee");
      } finally {
        setPageLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const data = await getDepartments();
        setDepartments(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingMeta(false);
      }
    };
    const loadShifts = async () => {
      try {
        const response = await api.get("work-calendar/shifts/");
        const shiftData = response.data.data || response.data.results || response.data || [];
        setShifts(Array.isArray(shiftData) ? shiftData : []);
      } catch (error) { console.error(error); }
    };
    loadDepartments();
    loadShifts();
  }, []);

  useEffect(() => {
    const loadDesignations = async () => {
      if (!form.department) {
        setDesignations([]);
        return;
      }
      try {
        const data = await getDesignations(form.department);
        setDesignations(data);
      } catch (error) {
        console.error(error);
      }
    };
    loadDesignations();
  }, [form.department]);

  useEffect(() => {
    const loadManagers = async () => {
      try {
        const data = await getReportingManagers(form.department, existingEmployee?.id);
        setReportingManagers(data);
      } catch (error) {
        console.error(error);
      }
    };
    loadManagers();
  }, [form.department, existingEmployee?.id]);

  const validate = () => {
    const errors = {};
    if (!form.first_name) errors.first_name = "First name is required";
    if (!form.last_name) errors.last_name = "Last name is required";
    if (!form.email) errors.email = "Email is required";
    if (!form.phone) errors.phone = "Phone is required";
    if (!form.department) errors.department = "Department is required";
    if (!form.designation) errors.designation = "Designation is required";
    if (!form.joining_date) errors.date_of_joining = "Joining date is required";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return "Please check the field errors marked below.";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setFieldErrors({ email: "Please enter a valid email address" });
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    if (fieldErrors[name] || fieldErrors.bank_account_number || fieldErrors.ifsc_code || fieldErrors.date_of_joining) {
      setFieldErrors((prev) => ({ ...prev, [name]: null, bank_account_number: null, ifsc_code: null, date_of_joining: null }));
    }
  };

  const toggleStatus = () => {
    setForm((prev) => ({
      ...prev,
      status: prev.status === EmployeeStatus.ACTIVE ? EmployeeStatus.INACTIVE : EmployeeStatus.ACTIVE,
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, photo: file, photoPreview: url }));
  };

  const handleDocumentChange = (index, field, value) => {
    const updatedDocs = form.documents.map((doc, i) =>
      i === index ? { ...doc, [field]: value } : doc
    );
    setForm((prev) => ({ ...prev, documents: updatedDocs }));
  };

  const addDocumentRow = () => {
    setForm((prev) => ({
      ...prev,
      documents: [...prev.documents, { type: "", file: null }],
    }));
  };

  const removeDocumentRow = (index) => {
    setForm((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  const buildFormData = (employeeData) => {
    const formData = new FormData();
    const nullableFks = ["reporting_manager", "department", "designation", "shift_id", "role_id", "date_of_birth", "gender"];
    Object.entries(employeeData).forEach(([key, value]) => {
      if (key === "documents") return;
      if (Array.isArray(value)) {
        if (key === "role_ids") {
          value.forEach((val) => formData.append("role_ids", val));
        }
        return;
      }
      if (value === null || value === undefined || value === "") {
        if (nullableFks.includes(key)) {
          formData.append(key, "");
        }
      } else {
        formData.append(key, value);
      }
    });
    employeeData.documents?.forEach((doc, index) => {
      // Send the existing document's id so the backend updates that
      // record instead of creating a duplicate / losing the association.
      if (doc.id) formData.append(`documents[${index}][id]`, doc.id);
      if (doc.type) formData.append(`documents[${index}][type]`, doc.type);
      if (doc.file instanceof File) formData.append(`documents[${index}][file]`, doc.file);
    });
    return formData;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) return setError(err);

    try {
      setSaving(true);
      const employeeData = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        gender: form.gender,
        date_of_birth: form.date_of_birth,
        department: form.department ? (typeof form.department === "object" ? form.department.id : parseInt(form.department, 10)) || null : null,
        designation: form.designation ? (typeof form.designation === "object" ? form.designation.id : parseInt(form.designation, 10)) || null : null,
        date_of_joining: form.joining_date,
        status: form.status,
        reporting_manager: form.reporting_manager ? (typeof form.reporting_manager === "object" ? form.reporting_manager.id : parseInt(form.reporting_manager, 10)) || null : null,
        shift_id: form.shift ? (typeof form.shift === "object" ? form.shift.id : parseInt(form.shift, 10)) || null : null,
        role_ids: Array.isArray(form.role_ids) ? form.role_ids : (form.role_id ? [form.role_id] : []),
        bank_account_number: form.account_number,
        ifsc_code: form.ifsc,
        bank_name: form.bank_name,
        emergency_contact_name: form.emergency_contact_name,
        emergency_contact_number: form.emergency_contact_number,
        remarks: form.remarks,
        documents: form.documents,
      };
      if (form.photo) employeeData.photo = form.photo;

      const formData = buildFormData(employeeData);

      if (isEdit) {
        await updateEmployee(id, formData);
        toast.success("Employee updated successfully");
        navigate("/employee");
      } else {
        const response = await createEmployee(formData);
        const newPassword = response?.data?.temporary_password || response?.data?.data?.temporary_password || response?.temporary_password;
        if (newPassword) {
          navigate("/employee", {
            state: {
              newPasswordData: {
                name: response?.data?.full_name || response?.data?.data?.full_name || response?.full_name || "New Employee",
                password: newPassword,
              },
            },
          });
        } else {
          toast.success("Employee created successfully");
          navigate("/employee");
        }
      }
    } catch (err) {
      const respData = err?.response?.data;
      const formatted = parseFieldErrors(err);
      const mapped = { ...formatted };
      if (formatted.shift_id) mapped.shift = formatted.shift_id;
      if (formatted.role_ids) mapped.role_ids = formatted.role_ids;
      if (formatted.role_id) mapped.role_ids = formatted.role_id;
      if (formatted.role) mapped.role_ids = formatted.role;
      if (formatted.bank_account_number) mapped.account_number = formatted.bank_account_number;
      if (formatted.ifsc_code) mapped.ifsc = formatted.ifsc_code;
      if (formatted.date_of_joining) mapped.date_of_joining = formatted.date_of_joining;
      if (formatted.joining_date) mapped.date_of_joining = formatted.joining_date;

      setFieldErrors(mapped);
      if (Object.keys(mapped).length > 0) {
        setError("Please check the field errors marked below.");
      } else {
        setError(respData?.message || respData?.detail || "Failed to save employee");
      }
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading || loadingMeta) {
    return (
      <div className="container employee-page">
        <div className="empty-state"><p>Loading…</p></div>
      </div>
    );
  }

  return (
    <div className="container employee-page">
      {error && <div className="form-error">{error}</div>}

      <div className="employee-page-card">
        <div className="employee-page-header">
          <div className="header-title-block">
            <h2>{isEdit ? "Edit Employee" : "Add New Employee"}</h2>
          </div>
          <button type="button" className="back-link" onClick={() => navigate("/employee")}>
             Back to Employees
          </button>
        </div>

        <div className="employee-page-body">

          {/* AVATAR */}
          <div className="avatar-upload-container">
            <div className="avatar-preview-circle" onClick={() => photoInputRef.current?.click()}>
              {form.photoPreview ? (
                <img src={form.photoPreview} alt="Profile preview" />
              ) : (
                <div className="avatar-placeholder">
                  <FaCamera size={22} />
                  <span>Upload</span>
                </div>
              )}
              <div className="avatar-hover-overlay">
                <FaCamera size={18} />
                <span>Change Photo</span>
              </div>
            </div>
            <input
              type="file"
              hidden
              accept="image/*"
              ref={photoInputRef}
              onChange={handlePhotoUpload}
            />
            <p className="avatar-caption">Click to upload a profile photo</p>
          </div>

          {/* PERSONAL */}
          <div className="form-section-card">
            <div className="section-title-premium"><FaUser /> Personal Information</div>
            <div className="grid-3">
              <div>
                <input className={fieldErrors.first_name ? "input-error" : ""} name="first_name" placeholder="First Name *" value={form.first_name} onChange={handleChange} />
                {fieldErrors.first_name && <div className="field-error">{fieldErrors.first_name}</div>}
              </div>
              <div>
                <input className={fieldErrors.last_name ? "input-error" : ""} name="last_name" placeholder="Last Name *" value={form.last_name} onChange={handleChange} />
                {fieldErrors.last_name && <div className="field-error">{fieldErrors.last_name}</div>}
              </div>
              <div>
                <input className={fieldErrors.email ? "input-error" : ""} name="email" placeholder="Email *" value={form.email} onChange={handleChange} />
                {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
              </div>
              <div>
                <input className={fieldErrors.phone ? "input-error" : ""} name="phone" placeholder="Phone *" value={form.phone} onChange={handleChange} />
                {fieldErrors.phone && <div className="field-error">{fieldErrors.phone}</div>}
              </div>
              <div>
                <Dropdown
                  options={GenderOptions}
                  value={form.gender}
                  placeholder="Select Gender"
                  onChange={(value) => setForm((prev) => ({ ...prev, gender: value }))}
                />
              </div>
              <div>
                <DatePicker
                  value={form.date_of_birth}
                  placeholder="Select Birthday"
                  maxDate={new Date().toISOString().split("T")[0]}
                  onChange={(value) => setForm((prev) => ({ ...prev, date_of_birth: value }))}
                />
              </div>
            </div>
          </div>

          {/* JOB */}
          <div className="form-section-card">
            <div className="section-title-premium"><FaBriefcase /> Job Information</div>
            <div className="grid-3">
              <div>
                <Dropdown
                  className={fieldErrors.department ? "input-error" : ""}
                  options={departments.map((d) => ({ label: d.name, value: d.id }))}
                  value={form.department}
                  placeholder="Select Department *"
                  onChange={(value) => {
                    setForm((prev) => ({ ...prev, department: value, designation: "", reporting_manager: "" }));
                    setFieldErrors(prev => ({ ...prev, department: null }));
                  }}
                />
                {fieldErrors.department && <div className="field-error">{fieldErrors.department}</div>}
              </div>
              <div>
                <Dropdown
                  className={fieldErrors.designation ? "input-error" : ""}
                  options={designations.map((d) => ({ label: d.name, value: d.id }))}
                  value={form.designation}
                  placeholder={!form.department ? "Select Department first" : "Select Designation *"}
                  onChange={(value) => {
                    setForm((prev) => ({ ...prev, designation: value }));
                    setFieldErrors(prev => ({ ...prev, designation: null }));
                  }}
                />
                {fieldErrors.designation && <div className="field-error">{fieldErrors.designation}</div>}
              </div>
             <div>
  <DatePicker
    className={(fieldErrors.date_of_joining || fieldErrors.joining_date) ? "input-error" : ""}
    value={form.joining_date}
    placeholder="Date of Joining *"
    maxDate={new Date().toISOString().split("T")[0]}
    onChange={(value) => {
      setForm((prev) => ({ ...prev, joining_date: value }));
      setFieldErrors((prev) => ({ ...prev, date_of_joining: null, joining_date: null }));
    }}
  />
  {(fieldErrors.date_of_joining || fieldErrors.joining_date) && <div className="field-error">{fieldErrors.date_of_joining || fieldErrors.joining_date}</div>}
</div>
              <div>
                <Dropdown
                  className={fieldErrors.reporting_manager ? "input-error" : ""}
                  options={reportingManagers.map((manager) => ({
                    label: manager.full_name || manager.name || `${manager.first_name || ""} ${manager.last_name || ""}`,
                    value: manager.id,
                  }))}
                  value={form.reporting_manager}
                  placeholder="Select Reporting Manager"
                  onChange={(value) => { setForm((prev) => ({ ...prev, reporting_manager: value })); if (fieldErrors.reporting_manager) setFieldErrors({ ...fieldErrors, reporting_manager: null }); }}
                />
                {fieldErrors.reporting_manager && <div className="field-error">{fieldErrors.reporting_manager}</div>}
              </div>
              {/* <div>
                <Dropdown
                  className={fieldErrors.shift ? "input-error" : ""}
                  options={shifts.map((s) => ({ label: s.name, value: s.id }))}
                  value={form.shift}
                  placeholder="Assign Shift"
                  onChange={(value) => { setForm((prev) => ({ ...prev, shift: value })); if (fieldErrors.shift) setFieldErrors({ ...fieldErrors, shift: null }); }}
                />
                {fieldErrors.shift && <div className="field-error">{fieldErrors.shift}</div>}
              </div> */}
              <div className="status-toggle">
                <div className={`toggle-switch ${form.status === EmployeeStatus.ACTIVE ? "active" : ""}`} onClick={toggleStatus}>
                  <div className="toggle-knob"></div>
                </div>
                <span className={`status-label ${form.status === EmployeeStatus.ACTIVE ? "active" : "inactive"}`}>{form.status === EmployeeStatus.ACTIVE ? "Active" : "Inactive"}</span>
              </div>
            </div>
          </div>

          {/* EMERGENCY */}
          <div className="form-section-card">
            <div className="section-title-premium"><FaPhoneAlt /> Emergency Contacts</div>
            <div className="grid-3">
              <div>
                <input className={fieldErrors.emergency_contact_name ? "input-error" : ""} name="emergency_contact_name" placeholder="Contact Name" value={form.emergency_contact_name} onChange={handleChange} />
                {fieldErrors.emergency_contact_name && <div className="field-error">{fieldErrors.emergency_contact_name}</div>}
              </div>
              <div>
                <input className={fieldErrors.emergency_contact_number ? "input-error" : ""} name="emergency_contact_number" placeholder="Contact Number" value={form.emergency_contact_number} onChange={handleChange} />
                {fieldErrors.emergency_contact_number && <div className="field-error">{fieldErrors.emergency_contact_number}</div>}
              </div>
            </div>
          </div>

          {/* BANK */}
          <div className="form-section-card">
            <div className="section-title-premium"><FaUniversity /> Bank Details</div>
            <div className="grid-3">
              <div>
                <input className={fieldErrors.bank_name ? "input-error" : ""} name="bank_name" placeholder="Bank Name" value={form.bank_name} onChange={handleChange} />
                {fieldErrors.bank_name && <div className="field-error">{fieldErrors.bank_name}</div>}
              </div>
              <div>
                <input className={fieldErrors.bank_account_number ? "input-error" : ""} name="account_number" placeholder="Account Number" value={form.account_number} onChange={handleChange} />
                {fieldErrors.bank_account_number && <div className="field-error">{fieldErrors.bank_account_number}</div>}
              </div>
              <div>
                <input className={fieldErrors.ifsc_code ? "input-error" : ""} name="ifsc" placeholder="IFSC Code" value={form.ifsc} onChange={handleChange} />
                {fieldErrors.ifsc_code && <div className="field-error">{fieldErrors.ifsc_code}</div>}
              </div>
            </div>
          </div>

          {/* DOCUMENTS */}
          <div className="form-section-card">
            <div className="section-title-premium"><FaFileAlt /> Documents</div>
            <div className="documents-stack">
              {form.documents.map((doc, index) => (
                <div className="document-row premium" key={doc.id ?? index}>
                  <Dropdown
                    options={DocumentTypeFormOptions}
                    value={doc.type}
                    placeholder="Select Document Type"
                    onChange={(value) => handleDocumentChange(index, "type", value)}
                  />
                  <label className="file-choose-premium" htmlFor={`file-${index}`}>
                    <span className="file-choose-icon"><FaCloudUploadAlt /></span>
                    <span className={`file-choose-text ${doc.file?.name || doc.original_filename ? "has-file" : ""}`}>
                      {doc.file?.name || doc.original_filename || "Choose a file to upload"}
                    </span>
                  </label>
                  <input
                    type="file"
                    id={`file-${index}`}
                    className="hidden-file-input"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleDocumentChange(index, "file", file);
                    }}
                  />
                  {form.documents.length > 1 && (
                    <button
                      type="button"
                      className="remove-doc-btn"
                      onClick={() => removeDocumentRow(index)}
                      aria-label="Remove document"
                    >
                      <FaTrashAlt size={12} />
                      <span>Remove</span>
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className="add-doc-btn" onClick={addDocumentRow}><FaPlus size={11} /> Add Document</button>
            </div>
          </div>

          {/* ADDITIONAL */}
          <div className="form-section-card">
            <div className="section-title-premium"><FaStickyNote /> Additional Notes</div>
            <div>
              <textarea className={fieldErrors.remarks ? "input-error" : ""} name="remarks" placeholder="Remarks" value={form.remarks} onChange={handleChange} />
              {fieldErrors.remarks && <div className="field-error">{fieldErrors.remarks}</div>}
            </div>
          </div>

        </div>

        <div className="employee-page-footer">
          <div className="employee-actions">
            <button className="secondary" onClick={() => navigate("/employee")} disabled={saving}>
              Cancel
            </button>
            <button className="primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Employee"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}